import { invokeFunction } from '@/lib/supabase';
import { addBreadcrumb } from '@/lib/sentry';
import type { PaidPlan } from '@/constants/billing';

// --- EDGE FUNCTION only (Stripe secret keys) ---

interface CheckoutSessionResponse {
  url: string;
}

interface PortalSessionResponse {
  url: string;
}

export async function createCheckoutSession(
  householdId: string,
  plan: PaidPlan,
): Promise<CheckoutSessionResponse> {
  addBreadcrumb('billing.checkout_started', {
    household_id: householdId,
    plan,
  });
  const { data, error } = await invokeFunction('billing-checkout', {
    body: { household_id: householdId, plan },
  });

  if (error) {
    addBreadcrumb('billing.checkout_failed', {
      household_id: householdId,
      plan,
    });
    throw error;
  }
  addBreadcrumb('billing.checkout_session_created', {
    household_id: householdId,
    plan,
  });
  return data as CheckoutSessionResponse;
}

export async function createPortalSession(
  householdId: string,
): Promise<PortalSessionResponse> {
  addBreadcrumb('billing.portal_started', { household_id: householdId });
  const { data, error } = await invokeFunction('billing-portal', {
    body: { household_id: householdId },
  });

  if (error) {
    addBreadcrumb('billing.portal_failed', { household_id: householdId });
    throw error;
  }
  addBreadcrumb('billing.portal_session_created', {
    household_id: householdId,
  });
  return data as PortalSessionResponse;
}

/**
 * Records the retention reason when an admin chooses to downgrade or
 * cancel. Best-effort analytics only — the ACTUAL plan change happens in
 * the Stripe Customer Portal (Portal-only model) and is synced back by
 * webhooks-stripe. Callers should not block the Portal redirect on this.
 */
export async function recordDowngradeReason(
  householdId: string,
  reasonKey: string,
  reasonOther: string | null,
): Promise<void> {
  addBreadcrumb('billing.downgrade_reason_recorded', {
    household_id: householdId,
    reason_key: reasonKey,
  });
  const { error } = await invokeFunction('billing-schedule-change', {
    body: {
      household_id: householdId,
      reason_key: reasonKey,
      reason_other: reasonOther,
    },
  });
  if (error) throw error;
}
